/* eslint-disable react/prop-types */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { axiosInstance } from '../../../utils/axios';
import './BbbRecordingPlayer.css';

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

/** Format milliseconds → "mm:ss" or "h:mm:ss" */
function formatTimeMs(ms) {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** Determine what content to show at a given timestamp. */
function getCurrentContentType(currentTimeMs, timeline) {
  // Priority 1: deskshare (screen share)
  const deskshare = timeline.deskshares?.find(
    (d) => currentTimeMs >= d.startMs && currentTimeMs <= d.endMs
  );
  if (deskshare) return { type: 'deskshare', data: deskshare };

  // Priority 2: external YouTube video
  const extVideo = timeline.externalVideos?.find(
    (v) => currentTimeMs >= v.startMs && currentTimeMs <= v.endMs
  );
  if (extVideo) return { type: 'externalVideo', data: extVideo };

  // Default: current slide
  const currentSlide = [...(timeline.slides || [])]
    .reverse()
    .find((s) => currentTimeMs >= s.timestampMs);
  return { type: 'slide', data: currentSlide || null };
}

/** Collect annotations visible at the current timestamp for a given slide. */
function getVisibleAnnotations(currentTimeMs, currentSlideNum, presentationId, annotations) {
  if (!annotations || annotations.length === 0) return [];

  const undone = new Set();
  const clearTimes = [];

  // First pass — collect undo / clear actions up to now for this slide
  for (const a of annotations) {
    if (a.timestampMs > currentTimeMs) continue;
    if (a.slideNum !== currentSlideNum || a.presentationId !== presentationId) continue;
    if (a.action === 'undo') undone.add(a.annotationId);
    if (a.action === 'clear') clearTimes.push(a.timestampMs);
  }

  // Second pass — collect visible "add" annotations
  return annotations.filter((a) => {
    if (a.action !== 'add') return false;
    if (a.timestampMs > currentTimeMs) return false;
    if (a.slideNum !== currentSlideNum) return false;
    if (a.presentationId !== presentationId) return false;
    if (undone.has(a.annotationId)) return false;
    if (a.undoTimestampMs && a.undoTimestampMs > 0 && currentTimeMs >= a.undoTimestampMs) return false;
    for (const ct of clearTimes) {
      if (ct > a.timestampMs && ct <= currentTimeMs) return false;
    }
    return true;
  });
}

/** Extract a YouTube video ID from various URL formats. */
function getYouTubeVideoId(url) {
  const match = url?.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([^?&#]+)/
  );
  return match ? match[1] : null;
}

/** Infer current slide context from annotations when no slide data is available. */
function inferCurrentSlideContext(currentTimeMs, annotations) {
  if (!annotations || annotations.length === 0) return null;
  let latest = null;
  for (const a of annotations) {
    if (a.action !== 'add') continue;
    if (a.timestampMs > currentTimeMs) break;
    latest = a;
  }
  if (!latest) return null;
  return { slideNum: latest.slideNum, presentationId: latest.presentationId };
}

/** Load the YouTube IFrame API (singleton). */
let ytApiPromise = null;
function loadYouTubeAPI() {
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve();
      return;
    }
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(tag, firstScript);
    window.onYouTubeIframeAPIReady = () => resolve();
  });
  return ytApiPromise;
}

// Available playback speeds
const SPEEDS = [0.5, 1, 1.25, 1.5, 2];

// ────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────

const BbbRecordingPlayer = ({ recordId, classId, onClose, meetingName }) => {
  // --- Logging State Refs ---
  const lastLoggedContentRef = useRef({ type: null, slideNum: null });
  const lastLoggedAnnRef = useRef({ count: -1, slideKey: null });

  useEffect(() => {
    console.log('[BbbPlayer][Init] Component mounted with props:', { recordId, classId, meetingName });
  }, [recordId, classId, meetingName]);

  // --- State ---
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [playing, setPlaying] = useState(false);
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  // --- Authenticated blob URL state ---
  const [webcamBlobUrl, setWebcamBlobUrl] = useState(null);
  const [deskshareBlobUrl, setDeskshareBlobUrl] = useState(null);
  const [slideBlobUrls, setSlideBlobUrls] = useState({});  // { slideUrl: blobUrl }

  // --- Refs ---
  const webcamRef = useRef(null);           // visible webcam <video>
  const deskshareRef = useRef(null);        // deskshare <video>
  const ytContainerRef = useRef(null);      // div for YouTube iframe
  const ytPlayerRef = useRef(null);         // YT.Player instance
  const animFrameRef = useRef(null);
  const prevContentRef = useRef(null);      // track content-type transitions
  const playerOverlayRef = useRef(null);    // for fullscreen
  const slideBlobCacheRef = useRef({});     // cache to avoid re-fetching slides

  // --- Construct asset base path (relative, for axiosInstance) ---
  const assetBasePath = timeline?.assetBaseUrl
    ? timeline.assetBaseUrl.replace(/^\//, '')
    : `recordings/bbb/asset/${classId}/${recordId}`;

  // ──────────────── Helper: fetch asset as authenticated blob URL ────────────────
  const fetchAuthenticatedBlob = useCallback(async (relativePath, signal) => {
    const fullPath = `/${assetBasePath}/${relativePath}`;
    console.log(`[BbbPlayer] Fetching authenticated asset: ${fullPath}`);
    try {
      const response = await axiosInstance.get(fullPath, {
        responseType: 'blob',
        signal,
      });
      const blobUrl = URL.createObjectURL(response.data);
      console.log(`[BbbPlayer] ✅ Asset fetched successfully: ${relativePath} (${response.data.size} bytes, type: ${response.data.type}) → ${blobUrl}`);
      return blobUrl;
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        console.log(`[BbbPlayer] Fetch cancelled for: ${relativePath}`);
        return null;
      }
      const status = err.response?.status || 'N/A';
      const statusText = err.response?.statusText || 'Unknown';
      console.error(`[BbbPlayer] ❌ Failed to fetch asset: ${relativePath}`, {
        url: fullPath,
        status,
        statusText,
        errorMessage: err.message,
        responseData: err.response?.data,
        headers: err.response?.headers,
      });
      return null;
    }
  }, [assetBasePath]);

  // ──────────────── Fetch timeline ────────────────
  const fetchTimeline = useCallback(async () => {
    const timelineUrl = `/recordings/bbb/timeline/${classId}/${recordId}`;
    console.log(`[BbbPlayer] Fetching timeline: ${timelineUrl}`);
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(timelineUrl);
      const data = response.data?.body || response.data;
      // ── Detailed timeline summary for diagnostics ──
      const deskshareRanges = (data.deskshares || []).map(d => `${formatTimeMs(d.startMs)}–${formatTimeMs(d.endMs)}`);
      const extVideoRanges = (data.externalVideos || []).map(v => `${formatTimeMs(v.startMs)}–${formatTimeMs(v.endMs)} [${v.url}]`);
      const uniquePresentations = [...new Set((data.slides || []).map(s => s.presentationId))];
      const annShapeTypes = {};
      (data.annotations || []).forEach(a => { annShapeTypes[a.shapeType || a.action] = (annShapeTypes[a.shapeType || a.action] || 0) + 1; });

      console.log(`[BbbPlayer] ✅ Timeline fetched successfully:`, {
        recordId: data.recordId,
        meetingId: data.meetingId,
        durationMs: data.durationMs,
        durationFormatted: formatTimeMs(data.durationMs || 0),
        slidesCount: data.slides?.length || 0,
        deskshareCount: data.deskshares?.length || 0,
        annotationsCount: data.annotations?.length || 0,
        externalVideosCount: data.externalVideos?.length || 0,
        assetBaseUrl: data.assetBaseUrl,
        defaultPresentationId: data.defaultPresentationId || 'NOT SET ⚠️',
        firstSlide: data.slides?.[0] || 'none',
      });

      console.log('[BbbPlayer][Timeline] 📋 DETAILED SUMMARY:');
      console.log(`  📎 Unique presentations: [${uniquePresentations.join(', ')}]`);
      console.log(`  🎞 All slides:`, (data.slides || []).map(s => `${formatTimeMs(s.timestampMs)}: ${s.presentationId}/slide-${s.slideNum} → ${s.slideUrl}`));
      console.log(`  🖥 Deskshare segments (${deskshareRanges.length}):`, deskshareRanges.length > 0 ? deskshareRanges : 'NONE — screen share will never appear');
      console.log(`  🎬 External videos (${extVideoRanges.length}):`, extVideoRanges.length > 0 ? extVideoRanges : 'NONE — YouTube embeds will never appear');
      console.log(`  ✏️ Annotation shapes:`, Object.keys(annShapeTypes).length > 0 ? annShapeTypes : 'NONE — no annotations will render');
      
      // Warnings for common issues
      if (!data.durationMs || data.durationMs === 0) console.warn('[BbbPlayer] ⚠️ WARNING: durationMs is 0 — seek bar won\'t work until webcam video loads');
      if (!data.slides || data.slides.length === 0) console.warn('[BbbPlayer] ⚠️ WARNING: No slides found — only whiteboard will be shown');
      if (!data.assetBaseUrl) console.warn('[BbbPlayer] ⚠️ WARNING: assetBaseUrl is empty — asset loading may fail');
      if (data.defaultPresentationId && uniquePresentations.length === 1 && uniquePresentations[0] === data.defaultPresentationId) {
        console.warn('[BbbPlayer] ⚠️ WARNING: Only the default BBB presentation exists — no custom slides were uploaded during this class');
      }
      setTimeline(data);
      setDurationMs(data.durationMs || 0);
    } catch (err) {
      const status = err.response?.status || 'N/A';
      console.error(`[BbbPlayer] ❌ Failed to fetch timeline:`, {
        url: timelineUrl,
        status,
        statusText: err.response?.statusText,
        errorMessage: err.message,
        responseData: err.response?.data,
      });
      setError('Failed to load recording data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [recordId, classId]);

  useEffect(() => {
    fetchTimeline();
    // Cleanup on unmount
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (ytPlayerRef.current) {
        try { ytPlayerRef.current.destroy(); } catch (_) { /* noop */ }
      }
      // Revoke all cached slide blob URLs
      Object.values(slideBlobCacheRef.current).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [fetchTimeline]);

  // ──────────────── Fetch webcam video as authenticated blob ────────────────
  useEffect(() => {
    if (!timeline) return;
    console.log('[BbbPlayer] Timeline loaded, fetching webcam blob...');
    const controller = new AbortController();
    let activeBlobUrl = null;

    (async () => {
      const blobUrl = await fetchAuthenticatedBlob('video/webcams.webm', controller.signal);
      if (!controller.signal.aborted && blobUrl) {
        console.log('[BbbPlayer] ✅ Webcam blob URL set:', blobUrl);
        setWebcamBlobUrl(blobUrl);
        activeBlobUrl = blobUrl;
      } else if (blobUrl) {
        // Fetch completed but component unmounted/re-rendered — revoke the orphaned blob
        URL.revokeObjectURL(blobUrl);
      }
    })();

    return () => {
      controller.abort();
      if (activeBlobUrl) {
        URL.revokeObjectURL(activeBlobUrl);
      }
    };
  }, [timeline, fetchAuthenticatedBlob]);

  // ──────────────── Fetch deskshare video as authenticated blob ────────────────
  useEffect(() => {
    if (!timeline || !timeline.deskshares || timeline.deskshares.length === 0) {
      if (timeline) console.log('[BbbPlayer] No deskshare segments found, skipping deskshare fetch');
      return;
    }
    console.log(`[BbbPlayer] ${timeline.deskshares.length} deskshare segment(s) found, fetching deskshare blob...`);
    const controller = new AbortController();
    let activeBlobUrl = null;

    (async () => {
      const blobUrl = await fetchAuthenticatedBlob('deskshare/deskshare.webm', controller.signal);
      if (!controller.signal.aborted && blobUrl) {
        console.log('[BbbPlayer] ✅ Deskshare blob URL set:', blobUrl);
        setDeskshareBlobUrl(blobUrl);
        activeBlobUrl = blobUrl;
      } else if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    })();

    return () => {
      controller.abort();
      if (activeBlobUrl) {
        URL.revokeObjectURL(activeBlobUrl);
      }
    };
  }, [timeline, fetchAuthenticatedBlob]);

  // ──────────────── Animation loop ────────────────
  // Reads webcam video currentTime and propagates it as the single source of truth.
  const tick = useCallback(() => {
    const vid = webcamRef.current;
    if (vid && !vid.paused) {
      setCurrentTimeMs(vid.currentTime * 1000);
    }
    animFrameRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    animFrameRef.current = requestAnimationFrame(tick);
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [tick]);

  // ──────────────── Derived content type ────────────────
  const content = useMemo(
    () => {
      if (!timeline) return { type: 'slide', data: null };
      const result = getCurrentContentType(currentTimeMs, timeline);
      return result;
    },
    [currentTimeMs, timeline]
  );

  // ──────────────── Sync deskshare video ────────────────
  useEffect(() => {
    const ds = deskshareRef.current;
    if (!ds || content.type !== 'deskshare') return;

    let accumulatedVideoTime = 0;
    for (let dsSegment of (timeline?.deskshares || [])) {
      if (currentTimeMs >= dsSegment.startMs && currentTimeMs <= dsSegment.endMs) {
        accumulatedVideoTime += (currentTimeMs - dsSegment.startMs) / 1000;
        break;
      }
      if (currentTimeMs > dsSegment.endMs) {
        accumulatedVideoTime += (dsSegment.endMs - dsSegment.startMs) / 1000;
      }
    }
    const targetSec = accumulatedVideoTime;

    // Only seek if substantially out of sync (>0.5s)
    if (Math.abs(ds.currentTime - targetSec) > 0.5) {
      ds.currentTime = targetSec;
    }
    ds.playbackRate = speed;
    if (playing && ds.paused) ds.play().catch(() => {});
    if (!playing && !ds.paused) ds.pause();
  }, [content, playing, speed, currentTimeMs, timeline]);

  // ──────────────── Sync YouTube player ────────────────
  useEffect(() => {
    if (content.type !== 'externalVideo') {
      // Leaving a YouTube segment — destroy the player
      if (prevContentRef.current === 'externalVideo' && ytPlayerRef.current) {
        try { ytPlayerRef.current.destroy(); } catch (_) { /* noop */ }
        ytPlayerRef.current = null;
      }
      prevContentRef.current = content.type;
      return;
    }

    const videoId = getYouTubeVideoId(content.data.url);
    if (!videoId) { prevContentRef.current = content.type; return; }

    const updates = content.data.updates || [];
    let latestUpdate = null;
    for (let u of updates) {
      if (u.timestampMs <= currentTimeMs) {
        latestUpdate = u;
      } else {
        break;
      }
    }

    let isYtPlaying = playing;
    let offsetSec = (currentTimeMs - content.data.startMs) / 1000;

    if (latestUpdate) {
      const elapsedSinceUpdate = (currentTimeMs - latestUpdate.timestampMs) / 1000;
      isYtPlaying = playing && latestUpdate.status === 'play';
      if (latestUpdate.status === 'pause') {
         offsetSec = latestUpdate.timeOffset;
      } else {
         offsetSec = latestUpdate.timeOffset + elapsedSinceUpdate * speed;
      }
    }

    // Already have a player — just sync
    if (ytPlayerRef.current) {
      const ytState = ytPlayerRef.current.getPlayerState?.();
      // Sync position if drifted
      const ytTime = ytPlayerRef.current.getCurrentTime?.() || 0;
      if (Math.abs(ytTime - offsetSec) > 1.5) {
        ytPlayerRef.current.seekTo(offsetSec, true);
      }
      if (isYtPlaying && ytState !== 1 /* PLAYING */) ytPlayerRef.current.playVideo();
      if (!isYtPlaying && ytState === 1) ytPlayerRef.current.pauseVideo();
      prevContentRef.current = content.type;
      return;
    }

    // Create a new YouTube player
    loadYouTubeAPI().then(() => {
      if (!ytContainerRef.current) return;
      ytPlayerRef.current = new window.YT.Player(ytContainerRef.current, {
        videoId,
        playerVars: {
          autoplay: isYtPlaying ? 1 : 0,
          controls: 0,
          modestbranding: 1,
          rel: 0,
          start: Math.floor(offsetSec),
        },
        events: {
          onReady: (e) => {
            e.target.setVolume(volume * 100);
            if (isYtPlaying) e.target.playVideo();
          },
        },
      });
    });

    prevContentRef.current = content.type;
  }, [content.type, content.data?.url, playing, currentTimeMs, speed, volume]);

  // ──────────────── Visible annotations ────────────────
  const visibleAnnotations = useMemo(() => {
    if (!timeline || content.type !== 'slide') return [];
    const anns = timeline.annotations || [];
    if (anns.length === 0) return [];

    if (content.data) {
      return getVisibleAnnotations(
        currentTimeMs,
        content.data.slideNum,
        content.data.presentationId,
        anns
      );
    }

    // No slide data available — infer current slide context from annotations
    const ctx = inferCurrentSlideContext(currentTimeMs, anns);
    if (!ctx) return [];
    return getVisibleAnnotations(currentTimeMs, ctx.slideNum, ctx.presentationId, anns);
  }, [currentTimeMs, content, timeline]);

  // ──────────────── Default presentation detection ────────────────
  const isDefaultSlideActive = useMemo(() => {
    if (!timeline || content.type !== 'slide' || !content.data) return false;
    // Use backend-provided defaultPresentationId if available
    if (timeline.defaultPresentationId) {
      return content.data.presentationId === timeline.defaultPresentationId;
    }
    return false;
  }, [timeline, content]);

  // ──────────────── Diagnostic Logging Hooks ────────────────
  useEffect(() => {
    const prev = lastLoggedContentRef.current;
    if (content.type === 'deskshare' && prev.type !== 'deskshare') {
      const d = content.data;
      console.log(`[BbbPlayer][Content] ▶ SWITCHING to DESKSHARE at ${formatTimeMs(currentTimeMs)} (segment: ${formatTimeMs(d.startMs)}–${formatTimeMs(d.endMs)})`);
      lastLoggedContentRef.current = { type: 'deskshare', slideNum: null };
    } else if (content.type === 'externalVideo' && prev.type !== 'externalVideo') {
      const v = content.data;
      console.log(`[BbbPlayer][Content] ▶ SWITCHING to EXTERNAL VIDEO at ${formatTimeMs(currentTimeMs)} — URL: ${v.url} (${formatTimeMs(v.startMs)}–${formatTimeMs(v.endMs)})`);
      lastLoggedContentRef.current = { type: 'externalVideo', slideNum: null };
    } else if (content.type === 'slide') {
      const slideNum = content.data?.slideNum ?? null;
      const presId = content.data?.presentationId ?? null;
      if (prev.type !== 'slide' || prev.slideNum !== slideNum) {
        console.log(`[BbbPlayer][Content] ▶ SHOWING SLIDE at ${formatTimeMs(currentTimeMs)} — presId: ${presId}, slideNum: ${slideNum}, slideUrl: ${content.data?.slideUrl || 'none'}`);
        lastLoggedContentRef.current = { type: 'slide', slideNum };
      }
    }
  }, [content, currentTimeMs]);

  useEffect(() => {
    if (content.type !== 'slide' || !content.data) return;
    const slideKey = `${content.data.presentationId}/${content.data.slideNum}`;
    const count = visibleAnnotations.length;
    
    if (lastLoggedAnnRef.current.count !== count || lastLoggedAnnRef.current.slideKey !== slideKey) {
      const shapeTypes = {};
      visibleAnnotations.forEach(a => { shapeTypes[a.shapeType] = (shapeTypes[a.shapeType] || 0) + 1; });
      console.log(`[BbbPlayer][Annotations] Slide ${slideKey}: ${count} visible annotations`, {
        shapeBreakdown: shapeTypes,
        sampleIds: visibleAnnotations.slice(0, 3).map(a => a.annotationId),
      });
      lastLoggedAnnRef.current = { count, slideKey };
    }
  }, [visibleAnnotations, content]);

  // ──────────────── Annotation viewBox for whiteboard ────────────────
  const annotationViewBox = useMemo(() => {
    if (visibleAnnotations.length > 0) {
      const withVB = visibleAnnotations.find((a) => a.viewBoxWidth && a.viewBoxHeight);
      if (withVB) return { width: withVB.viewBoxWidth, height: withVB.viewBoxHeight };
    }
    return { width: 1920, height: 1080 };
  }, [visibleAnnotations]);

  // ──────────────── Transport controls ────────────────
  const handlePlayPause = () => {
    const vid = webcamRef.current;
    if (!vid) return;
    if (vid.paused) {
      vid.play().catch(() => {});
      setPlaying(true);
    } else {
      vid.pause();
      setPlaying(false);
    }
  };

  const handleSeek = (e) => {
    const val = Number(e.target.value);
    const vid = webcamRef.current;
    if (vid) vid.currentTime = val / 1000;
    setCurrentTimeMs(val);
  };

  const handleSpeedChange = (e) => {
    const s = Number(e.target.value);
    setSpeed(s);
    if (webcamRef.current) webcamRef.current.playbackRate = s;
    if (deskshareRef.current) deskshareRef.current.playbackRate = s;
  };

  const handleVolumeChange = (e) => {
    const v = Number(e.target.value);
    setVolume(v);
    setMuted(v === 0);
    if (webcamRef.current) webcamRef.current.volume = v;
    if (ytPlayerRef.current?.setVolume) ytPlayerRef.current.setVolume(v * 100);
  };

  const handleMuteToggle = () => {
    const newMuted = !muted;
    setMuted(newMuted);
    if (webcamRef.current) webcamRef.current.muted = newMuted;
    if (ytPlayerRef.current) {
      newMuted ? ytPlayerRef.current.mute?.() : ytPlayerRef.current.unMute?.();
    }
  };

  const handleFullscreen = () => {
    const el = playerOverlayRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      el.requestFullscreen?.() || el.webkitRequestFullscreen?.();
    }
  };

  // Handle webcam video metadata load
  const handleWebcamLoaded = () => {
    const vid = webcamRef.current;
    if (!vid) return;
    console.log('[BbbPlayer] ✅ Webcam video metadata loaded:', {
      duration: vid.duration,
      videoWidth: vid.videoWidth,
      videoHeight: vid.videoHeight,
      readyState: vid.readyState,
      networkState: vid.networkState,
    });
    // Use video duration if timeline didn't provide one
    if (!durationMs && vid.duration && isFinite(vid.duration)) {
      console.log(`[BbbPlayer] Using webcam video duration: ${vid.duration}s (timeline had no duration)`);
      setDurationMs(vid.duration * 1000);
    }
    vid.playbackRate = speed;
    vid.volume = volume;
    vid.muted = muted;
  };

  // Handle webcam error
  const handleWebcamError = (e) => {
    const vid = webcamRef.current;
    const mediaError = vid?.error;
    console.error('[BbbPlayer] ❌ Webcam video error:', {
      errorCode: mediaError?.code,
      errorMessage: mediaError?.message,
      networkState: vid?.networkState,
      readyState: vid?.readyState,
      currentSrc: vid?.currentSrc,
      event: e,
    });
  };

  // Handle webcam ended
  const handleWebcamEnded = () => {
    console.log('[BbbPlayer] Webcam video ended');
    setPlaying(false);
    setCurrentTimeMs(durationMs);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;
      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault();
          handlePlayPause();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (webcamRef.current) {
            webcamRef.current.currentTime = Math.max(0, webcamRef.current.currentTime - 10);
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (webcamRef.current) {
            webcamRef.current.currentTime = Math.min(
              webcamRef.current.duration || Infinity,
              webcamRef.current.currentTime + 10
            );
          }
          break;
        case 'f':
          e.preventDefault();
          handleFullscreen();
          break;
        case 'm':
          e.preventDefault();
          handleMuteToggle();
          break;
        case 'Escape':
          e.preventDefault();
          onClose?.();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [playing, muted]);

  // ──────────────── Progress % for CSS variable ────────────────
  const progressPct = durationMs > 0 ? (currentTimeMs / durationMs) * 100 : 0;
  const volumePct = volume * 100;

  // ──────────────── Debug info (computed live) ────────────────
  const debugInfo = useMemo(() => {
    if (!showDebug || !timeline) return null;
    return {
      time: formatTimeMs(currentTimeMs),
      duration: formatTimeMs(durationMs),
      contentType: content.type,
      slidePresId: content.data?.presentationId || '—',
      slideNum: content.data?.slideNum ?? '—',
      slideUrl: content.data?.slideUrl || '—',
      isDefaultSlide: isDefaultSlideActive,
      visibleAnnotations: visibleAnnotations.length,
      totalSlides: timeline.slides?.length || 0,
      totalDeskshares: timeline.deskshares?.length || 0,
      totalExtVideos: timeline.externalVideos?.length || 0,
      totalAnnotations: timeline.annotations?.length || 0,
      defaultPresId: timeline.defaultPresentationId || '—',
      webcamLoaded: !!webcamBlobUrl,
      deskshareLoaded: !!deskshareBlobUrl,
      slideImgLoaded: !!slideImgUrl,
      playing,
      speed,
    };
  }, [showDebug, timeline, currentTimeMs, durationMs, content, isDefaultSlideActive, visibleAnnotations, webcamBlobUrl, deskshareBlobUrl, slideImgUrl, playing, speed]);

  // ──────────────── Fetch slide image as authenticated blob ────────────────
  const currentSlideUrl = content.type === 'slide' && content.data?.slideUrl
    ? content.data.slideUrl
    : null;

  useEffect(() => {
    if (!currentSlideUrl) return;
    // Already cached?
    if (slideBlobCacheRef.current[currentSlideUrl]) {
      console.log(`[BbbPlayer] Slide cache hit: ${currentSlideUrl}`);
      setSlideBlobUrls((prev) => ({
        ...prev,
        [currentSlideUrl]: slideBlobCacheRef.current[currentSlideUrl],
      }));
      return;
    }
    console.log(`[BbbPlayer] Fetching slide: ${currentSlideUrl}`);
    const controller = new AbortController();

    (async () => {
      const blobUrl = await fetchAuthenticatedBlob(currentSlideUrl, controller.signal);
      if (!controller.signal.aborted && blobUrl) {
        console.log(`[BbbPlayer] ✅ Slide loaded: ${currentSlideUrl}`);
        slideBlobCacheRef.current[currentSlideUrl] = blobUrl;
        setSlideBlobUrls((prev) => ({ ...prev, [currentSlideUrl]: blobUrl }));
      } else if (blobUrl) {
        // Slide changed before fetch completed — revoke the orphaned blob
        URL.revokeObjectURL(blobUrl);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [currentSlideUrl, fetchAuthenticatedBlob]);

  const slideImgUrl = currentSlideUrl ? (slideBlobUrls[currentSlideUrl] || null) : null;

  // ──────────────── Render ────────────────

  // Loading state
  if (loading) {
    return (
      <div className="bbb-player-overlay" ref={playerOverlayRef}>
        <div className="bbb-player-header">
          <div className="bbb-player-header-left">
            <button className="bbb-player-back-btn" onClick={onClose}>
              ← Back
            </button>
            <span className="bbb-player-title">Loading recording…</span>
          </div>
          <button className="bbb-player-close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="bbb-player-loading">
          <div className="bbb-player-spinner" />
          <span>Loading recording data…</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bbb-player-overlay" ref={playerOverlayRef}>
        <div className="bbb-player-header">
          <div className="bbb-player-header-left">
            <button className="bbb-player-back-btn" onClick={onClose}>
              ← Back
            </button>
            <span className="bbb-player-title">Recording Error</span>
          </div>
          <button className="bbb-player-close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="bbb-player-error">
          <div className="bbb-player-error-icon">⚠</div>
          <span>{error}</span>
          <button className="bbb-player-retry-btn" onClick={fetchTimeline}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const displayName =
    meetingName || timeline?.meetingName || 'Class Recording';

  // ──────────────── Precomputed annotation SVG ────────────────
  const showSlideImage = !!(slideImgUrl && content.data && !isDefaultSlideActive);
  
  const vbW = content.data?.viewBoxWidth || annotationViewBox.width;
  const vbH = content.data?.viewBoxHeight || annotationViewBox.height;

  const annotationsSvg = visibleAnnotations.length > 0 ? (
    <svg
      className="bbb-player-annotations-svg"
      viewBox={`0 0 ${vbW} ${vbH}`}
      preserveAspectRatio="xMidYMid meet"
    >
      {visibleAnnotations.map((ann) => {
        const hexColor =
          ann.color != null && !isNaN(Number(ann.color))
            ? `#${Number(ann.color).toString(16).padStart(6, '0')}`
            : ann.color || '#ff0000';

        if (ann.shapeType === 'text' && ann.text) {
          return (
            <text
              key={ann.annotationId}
              x={ann.x || 0}
              y={ann.y || 0}
              fill={hexColor}
              fontSize={ann.fontSize || 12}
              dominantBaseline="hanging"
              style={{ whiteSpace: 'pre' }}
            >
              {ann.text}
            </text>
          );
        }
        if (ann.shapeType === 'pencil' && ann.points) {
          const pts = ann.points.split(' ').map((p) => p.replace(',', ' ')).join(' ');
          return (
            <polyline
              key={ann.annotationId}
              points={pts}
              fill="none"
              stroke={hexColor}
              strokeWidth={ann.thickness || 2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        }
        if (ann.shapeType === 'line' && ann.points) {
          const coords = ann.points.split(' ').map((p) => p.split(','));
          if (coords.length >= 2) {
            return (
              <line
                key={ann.annotationId}
                x1={coords[0][0]}
                y1={coords[0][1]}
                x2={coords[coords.length - 1][0]}
                y2={coords[coords.length - 1][1]}
                stroke={hexColor}
                strokeWidth={ann.thickness || 2}
                strokeLinecap="round"
              />
            );
          }
        }
        if (ann.shapeType === 'rectangle' && ann.points) {
          const coords = ann.points.split(' ').map((p) => p.split(',').map(Number));
          if (coords.length >= 2) {
            const x = Math.min(coords[0][0], coords[1][0]);
            const y = Math.min(coords[0][1], coords[1][1]);
            const w = Math.abs(coords[1][0] - coords[0][0]);
            const h = Math.abs(coords[1][1] - coords[0][1]);
            return (
              <rect
                key={ann.annotationId}
                x={x} y={y} width={w} height={h}
                fill="none"
                stroke={hexColor}
                strokeWidth={ann.thickness || 2}
              />
            );
          }
        }
        if (ann.shapeType === 'ellipse' && ann.points) {
          const coords = ann.points.split(' ').map((p) => p.split(',').map(Number));
          if (coords.length >= 2) {
            const cx = (coords[0][0] + coords[1][0]) / 2;
            const cy = (coords[0][1] + coords[1][1]) / 2;
            const rx = Math.abs(coords[1][0] - coords[0][0]) / 2;
            const ry = Math.abs(coords[1][1] - coords[0][1]) / 2;
            return (
              <ellipse
                key={ann.annotationId}
                cx={cx} cy={cy} rx={rx} ry={ry}
                fill="none"
                stroke={hexColor}
                strokeWidth={ann.thickness || 2}
              />
            );
          }
        }
        // SVG path (from shapes.svg d-attribute)
        if (ann.shapeType === 'path' && ann.svgData) {
          return (
            <path
              key={ann.annotationId}
              d={ann.svgData}
              fill="none"
              stroke={hexColor}
              strokeWidth={ann.thickness || 2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        }
        // Fallback: draw as polyline
        if (ann.points) {
          const pts = ann.points.split(' ').map((p) => p.replace(',', ' ')).join(' ');
          return (
            <polyline
              key={ann.annotationId}
              points={pts}
              fill="none"
              stroke={hexColor}
              strokeWidth={ann.thickness || 2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        }
        return null;
      })}
    </svg>
  ) : null;

  return (
    <div className="bbb-player-overlay" ref={playerOverlayRef}>
      {/* ─── Header ─── */}
      <div className="bbb-player-header">
        <div className="bbb-player-header-left">
          <button className="bbb-player-back-btn" onClick={onClose}>
            ← Back
          </button>
          <span className="bbb-player-title">
            Class Recording: {displayName}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            className="bbb-player-back-btn"
            onClick={() => setShowDebug((v) => !v)}
            title="Toggle debug overlay"
            style={{ fontSize: '11px', padding: '4px 10px' }}
          >
            {showDebug ? '🛠 Hide Debug' : '🛠 Debug'}
          </button>
          <button className="bbb-player-close-btn" onClick={onClose}>
            ✕ Close
          </button>
        </div>
      </div>

      {/* ─── Debug Overlay ─── */}
      {showDebug && debugInfo && (
        <div style={{
          position: 'absolute', top: '50px', right: '12px', zIndex: 100,
          background: 'rgba(0,0,0,0.85)', color: '#0f0', fontFamily: 'monospace',
          fontSize: '11px', padding: '10px 14px', borderRadius: '8px',
          lineHeight: '1.6', maxWidth: '380px', pointerEvents: 'none',
          border: '1px solid #333', backdropFilter: 'blur(8px)',
        }}>
          <div style={{ color: '#6A4CFF', fontWeight: 'bold', marginBottom: '4px' }}>🛠 Player Debug Info</div>
          <div>⏱ Time: <span style={{color:'#fff'}}>{debugInfo.time} / {debugInfo.duration}</span></div>
          <div>📺 Content: <span style={{color: debugInfo.contentType === 'deskshare' ? '#ff0' : debugInfo.contentType === 'externalVideo' ? '#f80' : '#0f0'}}>{debugInfo.contentType}</span></div>
          <div>🎞 Slide: <span style={{color:'#fff'}}>pres={debugInfo.slidePresId}, num={debugInfo.slideNum}</span></div>
          <div>🖼 SlideURL: <span style={{color:'#aaa', wordBreak:'break-all'}}>{debugInfo.slideUrl}</span></div>
          <div>📝 IsDefault: <span style={{color: debugInfo.isDefaultSlide ? '#ff0' : '#0f0'}}>{String(debugInfo.isDefaultSlide)}</span></div>
          <div>✏️ Annotations: <span style={{color:'#fff'}}>{debugInfo.visibleAnnotations} visible</span></div>
          <div style={{ borderTop: '1px solid #333', marginTop: '4px', paddingTop: '4px' }}/>
          <div>📊 Timeline: {debugInfo.totalSlides} slides, {debugInfo.totalDeskshares} deskshares, {debugInfo.totalExtVideos} extVideos, {debugInfo.totalAnnotations} annotations</div>
          <div>🆔 DefaultPresId: <span style={{color:'#aaa'}}>{debugInfo.defaultPresId}</span></div>
          <div style={{ borderTop: '1px solid #333', marginTop: '4px', paddingTop: '4px' }}/>
          <div>📹 Webcam: <span style={{color: debugInfo.webcamLoaded ? '#0f0' : '#f00'}}>{debugInfo.webcamLoaded ? '✅ loaded' : '❌ not loaded'}</span></div>
          <div>🖥 Deskshare: <span style={{color: debugInfo.deskshareLoaded ? '#0f0' : '#ff0'}}>{debugInfo.deskshareLoaded ? '✅ loaded' : '⏳ not loaded'}</span></div>
          <div>🖼 SlideImg: <span style={{color: debugInfo.slideImgLoaded ? '#0f0' : '#ff0'}}>{debugInfo.slideImgLoaded ? '✅ loaded' : '⏳ not loaded'}</span></div>
          <div>▶️ Playing: {String(debugInfo.playing)} | ⚡ Speed: {debugInfo.speed}x</div>
        </div>
      )}


      {/* ─── Body ─── */}
      <div className="bbb-player-body">
        {/* Main Content Area */}
        <div className="bbb-player-main">
          {/* --- Slide / Whiteboard --- */}
          {content.type === 'slide' && (
            <div className="bbb-player-content-fade" key="slide">
              {showSlideImage ? (
                <div className="bbb-player-slide-container">
                  <img
                    className="bbb-player-slide-img"
                    src={slideImgUrl}
                    alt={`Slide ${(content.data?.slideNum ?? 0) + 1}`}
                    draggable={false}
                    onError={(e) => console.error('[BbbPlayer] ❌ Slide image failed to load:', {
                      slideUrl: currentSlideUrl,
                      blobUrl: slideImgUrl,
                      naturalWidth: e.target.naturalWidth,
                    })}
                  />
                  {annotationsSvg}
                </div>
              ) : (
                <div className="bbb-player-whiteboard-canvas">
                  {annotationsSvg}
                  {!annotationsSvg && (
                    <div className="bbb-player-whiteboard-placeholder">
                      <svg viewBox="0 0 24 24" width="48" height="48" fill="none"
                        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                        <rect x="2" y="3" width="20" height="14" rx="2" />
                        <path d="M2 17h20" />
                        <path d="M8 21h8" />
                        <path d="M12 17v4" />
                      </svg>
                      <span>Whiteboard</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* --- Deskshare (screen share) --- */}
          {content.type === 'deskshare' && (
            <div className="bbb-player-content-fade" key="deskshare">
              {deskshareBlobUrl ? (
                <video
                  ref={deskshareRef}
                  className="bbb-player-deskshare-video"
                  src={deskshareBlobUrl}
                  muted
                  playsInline
                  onError={(e) => {
                    const vid = deskshareRef.current;
                    console.error('[BbbPlayer] ❌ Deskshare video error:', {
                      errorCode: vid?.error?.code,
                      errorMessage: vid?.error?.message,
                      networkState: vid?.networkState,
                    });
                  }}
                />
              ) : (
                <div className="bbb-player-no-slide">Loading screen share…</div>
              )}
            </div>
          )}

          {/* --- YouTube embed --- */}
          {content.type === 'externalVideo' && (
            <div className="bbb-player-content-fade" key="youtube">
              <div className="bbb-player-youtube-container">
                <div ref={ytContainerRef}
                  style={{ width: '100%', height: '100%', minHeight: '300px' }} />
              </div>
            </div>
          )}
        </div>

        {/* Webcam Panel */}
        <div className="bbb-player-webcam">
          <div className="bbb-player-webcam-label">Webcam</div>
          {webcamBlobUrl ? (
            <video
              ref={webcamRef}
              src={webcamBlobUrl}
              playsInline
              onLoadedMetadata={handleWebcamLoaded}
              onEnded={handleWebcamEnded}
              onError={handleWebcamError}
            />
          ) : (
            <div className="bbb-player-no-slide" style={{ padding: '2rem', fontSize: '0.9rem' }}>
              Loading webcam…
            </div>
          )}
        </div>
      </div>

      {/* ─── Controls ─── */}
      <div className="bbb-player-controls">
        <div className="bbb-player-controls-inner">
          {/* Seek bar */}
          <div className="bbb-player-seek-row">
            <input
              type="range"
              className="bbb-player-seek"
              min={0}
              max={durationMs}
              value={currentTimeMs}
              onChange={handleSeek}
              style={{ '--progress': `${progressPct}%` }}
            />
            <span className="bbb-player-time-label">
              {formatTimeMs(currentTimeMs)} / {formatTimeMs(durationMs)}
            </span>
          </div>

          {/* Buttons */}
          <div className="bbb-player-buttons-row">
            <div className="bbb-player-buttons-left">
              {/* Play / Pause */}
              <button
                className="bbb-player-icon-btn bbb-player-play-btn"
                onClick={handlePlayPause}
                title={playing ? 'Pause (k)' : 'Play (k)'}
              >
                {playing ? '⏸' : '▶'}
              </button>

              {/* Mute toggle */}
              <button
                className="bbb-player-icon-btn"
                onClick={handleMuteToggle}
                title={muted ? 'Unmute (m)' : 'Mute (m)'}
              >
                {muted || volume === 0 ? '🔇' : volume < 0.5 ? '🔉' : '🔊'}
              </button>

              {/* Volume slider */}
              <input
                type="range"
                className="bbb-player-volume-slider"
                min={0}
                max={1}
                step={0.05}
                value={muted ? 0 : volume}
                onChange={handleVolumeChange}
                style={{ '--volume': `${muted ? 0 : volumePct}%` }}
              />
            </div>

            <div className="bbb-player-buttons-right">
              {/* Speed selector */}
              <select
                className="bbb-player-speed-select"
                value={speed}
                onChange={handleSpeedChange}
                title="Playback speed"
              >
                {SPEEDS.map((s) => (
                  <option key={s} value={s}>
                    {s}x
                  </option>
                ))}
              </select>

              {/* Fullscreen */}
              <button
                className="bbb-player-icon-btn"
                onClick={handleFullscreen}
                title="Fullscreen (f)"
              >
                ⛶
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BbbRecordingPlayer;
